/**
 * StreakRiskMount — bridge between the WS `streak_risk_member` verb and
 * the leader strip's <StreakRiskFallbackBadge> chips on the tablet idle
 * dashboard (Plan 02-17 Task 2 / D-08 / Plan 02-13 fallback path).
 *
 * The streakRiskNudge job (Plan 02-06) broadcasts
 *   { topic:'chores', event:'streak_risk_member', data:{ memberId } }
 * for every member whose 20:00 push could not be delivered (zero live
 * subscriptions or notifyMember threw). On the always-on kitchen tablet
 * the dashboard receives that frame, sets sessionStorage flag
 * `streak-risk:<memberId>='1'`, and renders a small chip per affected
 * member near the leader strip.
 *
 * Members are sourced from the weekly leaderboard payload — every family
 * member with at least one weekly row appears, and the chip only renders
 * when its sessionStorage flag is set (the badge component itself
 * short-circuits to null otherwise — see Plan 02-13). This keeps the
 * mount deterministic without requiring a separate member-listing API
 * on the authless tablet surface.
 *
 * Day rollover: the badge flag is one-shot per Oslo date. Plan 02-13's
 * badge reads the flag at mount; we do not bother clearing the flag
 * because each new sweep at 20:00 the next day re-broadcasts and the
 * sessionStorage entries from the prior day are stale — the badge will
 * keep showing until the tab reloads or the user clears storage. This
 * matches the "until next sweep" cadence STRK-04 promises.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useWeeklyLeaderboard } from '../leaderboard/useLeaderboardQueries';
import { StreakRiskFallbackBadge } from '../streaks/StreakRiskFallbackBadge';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

const FLAG_PREFIX = 'streak-risk:';
function setFlag(memberId: string): void {
  try {
    globalThis.sessionStorage?.setItem(`${FLAG_PREFIX}${memberId}`, '1');
  } catch {
    // sessionStorage may be unavailable (private browsing edge cases) —
    // we still keep the in-memory atRisk set so the badge mounts.
  }
}

export function StreakRiskMount(): ReactNode {
  // In-memory mirror — re-renders when the WS handler adds a memberId.
  // sessionStorage is the persistence layer; the Set drives React updates.
  const [atRisk, setAtRisk] = useState<Set<string>>(() => new Set());
  const leaderQuery = useWeeklyLeaderboard();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: { memberId?: string };
        };
        if (msg?.topic !== 'chores') return;
        if (msg.event !== 'streak_risk_member') return;
        const memberId = msg.data?.memberId;
        if (typeof memberId !== 'string' || memberId.length === 0) return;
        setFlag(memberId);
        setAtRisk((prev) => {
          if (prev.has(memberId)) return prev;
          const next = new Set(prev);
          next.add(memberId);
          return next;
        });
      } catch {
        // Drop malformed frames silently — Plan 05 server-side validation
        // means anything malformed reaching us is stray noise.
      }
    };

    return openReconnectingSocket({ topics: ['chores'], onMessage });
  }, []);

  // Resolve memberId → nick using the weekly leaderboard payload.
  const rows = leaderQuery.data ?? [];
  const idToNick = new Map(rows.map((r) => [r.memberId, r.short_name]));

  if (atRisk.size === 0) return null;

  return (
    <div
      data-testid="streak-risk-mount"
      className="flex flex-col gap-3"
    >
      {Array.from(atRisk).map((memberId) => (
        <StreakRiskFallbackBadge
          key={memberId}
          memberId={memberId}
          nick={idToNick.get(memberId) ?? memberId}
        />
      ))}
    </div>
  );
}
